import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Input from './shared/Input';
import Textarea from './shared/Textarea';
import Select from './shared/Select';
import Card from './shared/Card';
import Alert from './shared/Alert';
import Badge from './shared/Badge';
import InfoRow from './shared/InfoRow';
import Toggle from './shared/Toggle';
import Spinner from './shared/Spinner';

const SOURCE_TYPE_OPTIONS = [
    { value: 'text', label: 'Text' },
    { value: 'url', label: 'URL' },
    { value: 'file', label: 'File' },
];

const STATUS_BADGE = {
    indexed: 'success',
    pending: 'warning',
    failed: 'danger',
};

export default function AdminRAGDocumentForm() {
    const { collectionId, documentId } = useParams();
    const navigate = useNavigate();
    const isCreate = !!collectionId && !documentId;
    const isView = !!documentId;

    const [data, setData] = useState(null);
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [indexing, setIndexing] = useState(false);
    const [metadataRows, setMetadataRows] = useState([]);

    useEffect(() => { loadData(); }, [collectionId, documentId]);

    const loadData = async () => {
        try {
            setLoading(true);
            if (isCreate) {
                const initial = {
                    title: '',
                    source_type: 'text',
                    content: '',
                    source_ref: '',
                    metadata: {},
                    auto_index: true,
                };
                setData(initial);
                setFormData(initial);
                setMetadataRows([]);
            } else {
                const doc = await adminApi.getRAGDocument(documentId);
                setData(doc);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const metadata = metadataRows.reduce((acc, r) => {
                if (r.key.trim() && r.value.trim()) acc[r.key.trim()] = r.value.trim();
                return acc;
            }, {});
            const payload = {
                title: formData.title,
                source_type: formData.source_type,
                auto_index: formData.auto_index,
                metadata,
            };

            if (formData.source_type === 'text') {
                payload.content = formData.content;
            } else {
                payload.source_ref = formData.source_ref;
            }

            await adminApi.createRAGDocument(collectionId, payload);
            navigate(`/admin/rag/manage/${collectionId}`);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleIndex = async () => {
        setIndexing(true);
        try {
            const result = await adminApi.indexRAGDocument(documentId);
            setSuccessMessage(`Indexed: ${result.chunks_indexed} chunks (${result.status})`);
            loadData();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        } finally {
            setIndexing(false);
        }
    };

    if (loading) return <Spinner size="lg" text="Loading..." center />;

    const backPath = isCreate ? `/admin/rag/manage/${collectionId}` : `/admin/rag/manage/${data?.collection_id}`;
    const title = isCreate ? 'Add Document' : data?.title;

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link
                    to={backPath}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                </Link>
                <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
                {isView && (
                    <div className="ml-auto flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleIndex}
                            loading={indexing}
                        >
                            Reindex
                        </Button>
                    </div>
                )}
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            {isView ? (
                <div className="space-y-4">
                    <Card title="Document Info">
                        <InfoRow label="ID" value={data.id} mono />
                        <InfoRow label="Collection" value={data.collection_id} mono />
                        <InfoRow label="Title" value={data.title} />
                        <InfoRow label="Source Type" value={
                            <Badge variant="outline">{data.source_type}</Badge>
                        } />
                        {data.source_ref && (
                            <InfoRow label="Source Ref" value={data.source_ref} mono />
                        )}
                        <InfoRow label="Status" value={
                            <Badge variant={STATUS_BADGE[data.status] || 'default'} dot>
                                {data.status}
                            </Badge>
                        } />
                        <InfoRow label="Chunks" value={data.chunk_count} />
                    </Card>

                    {data.metadata && Object.keys(data.metadata).length > 0 && (
                        <Card title="Metadata">
                            {Object.entries(data.metadata).map(([k, v]) => (
                                <InfoRow key={k} label={k} value={typeof v === 'object' ? JSON.stringify(v) : String(v)} />
                            ))}
                        </Card>
                    )}

                    <Card title="Content Preview">
                        <div className="max-h-96 overflow-y-auto">
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                                {data.content || '(empty)'}
                            </pre>
                        </div>
                    </Card>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Card title="Document Info">
                        <Input
                            label="Title"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            placeholder="Document title"
                            required
                        />
                        <Select
                            label="Source Type"
                            value={formData.source_type}
                            onChange={(e) => handleChange('source_type', e.target.value)}
                            options={SOURCE_TYPE_OPTIONS}
                        />
                    </Card>

                    <Card title="Content">
                        {formData.source_type === 'text' ? (
                            <Textarea
                                label="Content"
                                value={formData.content}
                                onChange={(e) => handleChange('content', e.target.value)}
                                placeholder="Paste your text content here..."
                                rows={12}
                                monospace
                                required
                            />
                        ) : formData.source_type === 'url' ? (
                            <Input
                                label="URL"
                                value={formData.source_ref}
                                onChange={(e) => handleChange('source_ref', e.target.value)}
                                placeholder="https://example.com/article"
                                required
                            />
                        ) : (
                            <Input
                                label="File Path"
                                value={formData.source_ref}
                                onChange={(e) => handleChange('source_ref', e.target.value)}
                                placeholder="/path/to/file.pdf"
                                required
                            />
                        )}
                    </Card>

                    <Card title="Metadata" description="Custom key-value pairs stored with each chunk for filtering">
                        {metadataRows.length === 0 ? (
                            <button
                                type="button"
                                onClick={() => setMetadataRows([{ key: '', value: '' }])}
                                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                + Add metadata
                            </button>
                        ) : (
                            <div className="space-y-2">
                                {metadataRows.map((row, idx) => (
                                    <div key={idx} className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={row.key}
                                            onChange={(e) => {
                                                const updated = [...metadataRows];
                                                updated[idx] = { ...updated[idx], key: e.target.value };
                                                setMetadataRows(updated);
                                            }}
                                            placeholder="key"
                                            className="h-8 flex-1 rounded border border-gray-200 px-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1"
                                        />
                                        <span className="text-xs text-gray-300">=</span>
                                        <input
                                            type="text"
                                            value={row.value}
                                            onChange={(e) => {
                                                const updated = [...metadataRows];
                                                updated[idx] = { ...updated[idx], value: e.target.value };
                                                setMetadataRows(updated);
                                            }}
                                            placeholder="value"
                                            className="h-8 flex-1 rounded border border-gray-200 px-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setMetadataRows(metadataRows.filter((_, i) => i !== idx))}
                                            className="text-gray-300 hover:text-red-400 transition-colors p-1"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setMetadataRows([...metadataRows, { key: '', value: '' }])}
                                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    + Add row
                                </button>
                            </div>
                        )}
                    </Card>

                    <Card title="Options">
                        <Toggle
                            label="Auto-index after creation"
                            checked={formData.auto_index}
                            onChange={(val) => handleChange('auto_index', val)}
                        />
                    </Card>

                    <div className="flex items-center justify-end gap-2 pt-2 pb-4">
                        <Button variant="ghost" type="button" onClick={() => navigate(backPath)}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={saving}>
                            Add Document
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
}
